import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  ETrafficEvents,
  IReportTable,
  TRAFFIC_EVENT_COLUMNS,
  TReportData,
  TStatsBySplit,
} from "model/TrafficPage/trafficTable";
import {
  convertCreativeSize,
  isSplitChartView,
} from "routes/TrafficRefactor/helpers";
import {
  getTrafficQuery,
  getTrafficQueryFromTable,
  populateFullDateRange,
} from "./helpers";
// TODO: perhaps move this somewhere shared?
import { getQuickLinks } from "components/DateTime/quickLinksModelUtc";
import orderBy from "lodash/orderBy";
import { DEFAULT_SORT_BY, TRAFFIC_FILTERS_DICTIONARY } from "model/TrafficPage";
import { normalizeChartData } from "model/TrafficPage/normalizeChartData";
import { DataDisplayMode } from "model/report";
import { TimeRange } from "model/report/dateRange";
import {
  ReportNormalizer,
  parseWithNames,
  sortColumnAlpha,
  sortColumnAlphaNumeric,
  sortColumnNumeric,
} from "model/report/normalization";
import {
  DimensionIds,
  convertBucketBidFloor,
} from "model/reporterDb/dbTableTraffic";
import { GRANULARITY_TYPES } from "model/reporterDb/granularity";
import { IRootState } from "redux/reducer";
import { fetchReport } from "services/reporter/fetch";

// NOTE: This amount is 5 items more than is defined in Traffic/SplitChart
// This is to make sure it still triggers UI warning that the data is capped
const CAPPED_DATA = 15;

// Custom identifyConvertData function for Traffic
// cause in Traffic 2 groups might be combined into 1 (example: CREATIVE_FORMAT_SIZE)
const customIdentifyConvertData = (parsedData, columnIds) => {
  return parsedData.map((row: string[]) => {
    const newRow: Record<string, any> = {};
    let shift = 0;
    columnIds.forEach((colName, colIndex) => {
      const i = colIndex + shift;
      if (TRAFFIC_FILTERS_DICTIONARY[colName]?.combineDimensionIds?.length) {
        const { combineDimensionIds, isEmptyValue } =
          TRAFFIC_FILTERS_DICTIONARY[colName];
        const dId = combineDimensionIds?.find((d, j) => {
          return !isEmptyValue?.(row[i + j]);
        });
        if (dId) {
          newRow[colName] = newRow[dId] =
            row[i + combineDimensionIds?.indexOf(dId)];
        } else {
          newRow[colName] = undefined;
        }
        //next colName will be shifted: cause this colName took(combined) several values in row, instead of just 1
        shift +=
          (TRAFFIC_FILTERS_DICTIONARY[colName].combineDimensionIds as string[])
            .length - 1;
      } else {
        newRow[colName] = row[i];
      }
    });

    if (newRow[DimensionIds.CREATIVE_FORMAT_SIZE]) {
      newRow[DimensionIds.CREATIVE_FORMAT_SIZE] = convertCreativeSize(
        newRow[DimensionIds.CREATIVE_FORMAT_SIZE]
      );
    }
    return newRow;
  });
};

/**
 * SPLIT CHART ONLY: Processing split chart data will be diverted to a web worker
 * @param table
 * @param orderedByVolume Top data ordered by requests that we can pass to
 * the web worker in order to get only the top CAPPED_DATA items processed.
 */
const onReportReady =
  (table: IReportTable, orderedByVolume?: string[]) =>
  (rawTextData: string): Promise<TReportData> => {
    const { granularity, splits, view, dateRange } = table;

    const groupBy =
      granularity === "ALL"
        ? splits.filter((s) => s.id !== DimensionIds.GRANULARITY_COLUMN_ID)
        : splits;

    // SPLIT CHART DATA -> send to web worker
    if (view === DataDisplayMode.charts && groupBy.length === 2) {
      const topCappedData = orderedByVolume?.slice(0, CAPPED_DATA);

      //eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve) => {
        const worker = new Worker(
          new URL("../../workers/worker.ts", import.meta.url),
          { type: "module" }
        );
        worker.postMessage(["traffic", rawTextData, groupBy, topCappedData]);
        worker.addEventListener("message", (event) => {
          const { groupBy, data: formattedData } = event.data;

          // NOTE: only limited processing can be done in web worker
          // as it doesn't like high amount of imported modules
          const filteredData = formattedData.map((dataRow) => ({
            ...dataRow,
            data: dataRow.data.filter((d) => !!d.GRANULARITY),
          }));

          const fullRangeData = populateFullDateRange(
            filteredData,
            dateRange.dates,
            granularity,
            groupBy[1].id
          );

          const sortedData = fullRangeData.sort(
            (a, b) => b[DEFAULT_SORT_BY] - a[DEFAULT_SORT_BY]
          );

          const normalizedData = sortedData.map((dataRow) => ({
            ...dataRow,
            data: dataRow.data.map(
              normalizeChartData(granularity, "GRANULARITY")
            ),
          }));

          resolve({ groupBy, data: normalizedData });
        });
      });
    }

    const normalizeValuesFnDict = {
      bidFloor: (val, row) =>
        convertBucketBidFloor(val, { requests: row.requests }),
    };

    return new Promise<TReportData>((resolve) => {
      const normalizedData = new ReportNormalizer({
        measures: [
          ...groupBy.map(({ id, name }) => ({
            id,
            label: name,
            columnType: "Dimension",
          })),
          ...TRAFFIC_EVENT_COLUMNS,
        ],
        granularity,
        groupBy: groupBy.map(({ id }) => id),
        rawTextData,
        view,
        dateRange: dateRange.dates,
        normalizeValuesFnDict,
        customHelperFns: {
          identifyConvertData: customIdentifyConvertData,
        },
      }).normalize();

      const data = normalizedData.sort(
        (a, b) => b[DEFAULT_SORT_BY] - a[DEFAULT_SORT_BY]
      );

      resolve({ groupBy, data });
    });
  };

const fetchReportData = ({
  table,
  orderedByVolume,
}: {
  table: IReportTable;
  orderedByVolume?: string[];
}): Promise<[IReportTable, TReportData]> => {
  const tableWithUpdatedDates = { ...table };
  const quickLinks = getQuickLinks();

  if (![TimeRange.custom, TimeRange.allTime].includes(table.dateRange.range)) {
    const ql = quickLinks[table.dateRange.range];
    if (ql?.value?.[0] && ql?.value?.[1]) {
      tableWithUpdatedDates.dateRange = {
        ...tableWithUpdatedDates.dateRange,
        dates: [ql.value[0], ql?.value[1]],
      };
    }
  }

  // Remove filters with null value
  const noNullFilters = {};

  for (const filter of Object.keys(tableWithUpdatedDates.filters)) {
    if (Array.isArray(tableWithUpdatedDates.filters[filter].value)) {
      noNullFilters[filter] = {
        ...tableWithUpdatedDates?.filters[filter],
        value: tableWithUpdatedDates?.filters[filter]?.value?.filter(
          (value) => value !== null
        ),
      };
    } else {
      noNullFilters[filter] = tableWithUpdatedDates.filters[filter];
    }
  }

  tableWithUpdatedDates.filters = noNullFilters;

  return fetchReport({ query: getTrafficQueryFromTable(tableWithUpdatedDates) })
    .then((resp) => resp.text())
    .then(onReportReady(tableWithUpdatedDates, orderedByVolume))
    .then((data) => [tableWithUpdatedDates, data]);
};

const fetchReportDataRefactored = ({
  table,
  orderedByVolume,
}: {
  table: IReportTable;
  orderedByVolume?: string[];
}): Promise<[IReportTable, TReportData]> => {
  const tableWithUpdatedDates = { ...table };

  // Is REMOVED during refactoring, moved to 'normalizeFromStored.ts' in model:

  // 1) Updating dates based on range (For example, Yesterday value is different every next day when report is run)
  // const quickLinks = getQuickLinks();
  // if (![TimeRange.custom, TimeRange.allTime].includes(table.dateRange.range)) {
  //   const ql = quickLinks[table.dateRange.range];
  //   if (ql?.value?.[0] && ql?.value?.[1]) {
  //     tableWithUpdatedDates.dateRange = {
  //       ...tableWithUpdatedDates.dateRange,
  //       dates: [ql.value[0], ql?.value[1]],
  //     };
  //   }
  // }

  // 2) Remove filters with null value
  // const noNullFilters = {};

  // for (const filter of Object.keys(tableWithUpdatedDates.filters)) {
  //   if (Array.isArray(tableWithUpdatedDates.filters[filter].value)) {
  //     noNullFilters[filter] = {
  //       ...tableWithUpdatedDates?.filters[filter],
  //       value: tableWithUpdatedDates?.filters[filter]?.value?.filter(
  //         (value) => value !== null
  //       ),
  //     };
  //   } else {
  //     noNullFilters[filter] = tableWithUpdatedDates.filters[filter];
  //   }
  // }

  // tableWithUpdatedDates.filters = noNullFilters;

  return fetchReport({ query: getTrafficQueryFromTable(tableWithUpdatedDates) })
    .then((resp) => resp.text())
    .then(onReportReady(tableWithUpdatedDates, orderedByVolume))
    .then((data) => [tableWithUpdatedDates, data]);
};

/**
 * SPLIT CHART ONLY: Fetches TOTAL stats for each line displayed in Split Chart View
 * @param table
 */
const fetchStats = (
  organizationId: string | undefined,
  accountId: string | undefined,
  table: IReportTable
): Promise<{
  statsBySplit: TStatsBySplit;
  orderedByVolume: string[];
}> => {
  const { splits, filters, dateRange } = table;
  // NOTE: splits[0] is GRANULARITY and only 1 other is allowed
  const selectedSplit = splits[1].id;

  return fetchReport({
    query: getTrafficQuery({
      splits,
      filterBy: filters,
      dateRange: dateRange.dates,
      granularity: GRANULARITY_TYPES.ALL,
    }),
  })
    .then((resp) => resp.text())
    .then((rawData) => {
      return parseWithNames(rawData, [
        selectedSplit,
        ...TRAFFIC_EVENT_COLUMNS.map(({ id }) => id),
      ]);
    })
    .then((reporterData) => {
      const normalized: any[] = reporterData.map(
        normalizeChartData(GRANULARITY_TYPES.ALL)
      );

      const sortedByRequests = orderBy(
        normalized,
        [ETrafficEvents.requestsWithLogVolume],
        ["desc"]
      );

      const statsBySplit: TStatsBySplit = {};
      sortedByRequests.forEach((d) => {
        statsBySplit[d[selectedSplit] || "UNKNOWN"] = d;
      });

      const topDataByRequestVolume = sortedByRequests.map((d) =>
        d[selectedSplit].toString()
      );

      return {
        statsBySplit,
        orderedByVolume: topDataByRequestVolume,
      };
    })
    .catch((e) => {
      return e;
    });
};

export const setTableWithData = createAsyncThunk(
  "traffic/setTableWithData",
  async (table: IReportTable, thunkAPI) => {
    const { splits = [], view } = table;
    const state = thunkAPI.getState() as IRootState;
    const { organizationId, accountId } = state.auth;

    // If view is SplitChart fetch stats per line
    if (isSplitChartView(view, splits)) {
      const { statsBySplit, orderedByVolume } = await fetchStats(
        organizationId,
        accountId,
        table
      );
      const [updatedTable, reportData] = await fetchReportData({
        table,
        orderedByVolume,
      });

      return {
        ...updatedTable,
        reportData,
        statsBySplit,
        orderedByVolume,
      };
    }

    const [updatedTable, reportData] = await fetchReportData({
      table,
    });
    return { ...updatedTable, reportData };
  }
);

export const updateTableReportData = createAsyncThunk(
  "traffic/updateTableReportData",
  async ({ table }: { table: IReportTable }, thunkAPI) => {
    const { splits = [], view } = table;
    const state = thunkAPI.getState() as IRootState;
    const { organizationId, accountId } = state.auth;

    // If view is SplitChart fetch stats per line
    if (isSplitChartView(view, splits)) {
      const { statsBySplit, orderedByVolume } = await fetchStats(
        organizationId,
        accountId,
        table
      );
      const [updatedTable, reportData] = await fetchReportData({
        table,
        orderedByVolume,
      });

      return {
        ...updatedTable,
        reportData,
        statsBySplit,
        orderedByVolume,
      };
    }
    const [updatedTable, reportData] = await fetchReportData({
      table,
    });
    return { ...updatedTable, reportData };
  }
);

const SORT_METHOD_DICTIONARY = {
  [ETrafficEvents.bidFloor]: sortColumnNumeric,
  [ETrafficEvents.requestsWithLogVolume]: sortColumnNumeric,
  [DimensionIds.CREATIVE_FORMAT_SIZE]: sortColumnAlphaNumeric,
};

export const sortReportData = createAsyncThunk(
  "traffic/sortReportData",
  async (
    {
      tableId,
      sortBy,
    }: { tableId: string | number; sortBy: { id: string; desc: boolean } },
    thunkAPI
  ) => {
    const { id, desc } = sortBy;
    const state = thunkAPI.getState() as IRootState;
    const selectedTable = state.trafficPage.tables.find(
      (tb) => tb.id.toString() === tableId.toString()
    );

    const customSortMethod = SORT_METHOD_DICTIONARY[id] || sortColumnAlpha;

    const data = selectedTable?.reportData?.data
      .slice()
      .sort(customSortMethod(id, desc));

    return { data, sortBy: id, desc };
  }
);

export const fetchTrafficDataKeepForRefactoring = {
  fetchReportData: fetchReportDataRefactored,
  fetchStats,
};
